import { Injectable } from '@angular/core';
import { META_APP_ID, META_CONFIG_ID, META_GRAPH_VERSION, getWhatsAppFeatureType } from '@dental-ui/shared/consts';
import { WhatsAppConfigData, WhatsAppSignupResult } from '../models/whatsapp.models';

interface FbLoginResponse {
  authResponse?: {
    code?: string;
  };
  status?: string;
}

interface WaEmbeddedSignupMessage {
  type?: string;
  event?: string;
  version?: number;
  data?: {
    phone_number_id?: string;
    waba_id?: string;
    current_step?: string;
    error_message?: string;
  };
}

declare global {
  interface Window {
    FB?: {
      init(params: { appId: string; cookie?: boolean; xfbml?: boolean; version: string }): void;
      login(callback: (response: FbLoginResponse) => void, options?: Record<string, unknown>): void;
    };
    fbAsyncInit?: () => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class MetaSdkService {
  private sdkLoadPromise: Promise<void> | null = null;
  private sdkInitialized = false;

  loadAndInit(config: WhatsAppConfigData | null = null): Promise<void> {
    const appId = config?.metaAppId || META_APP_ID;
    const version = config?.graphVersion || META_GRAPH_VERSION;

    if (this.sdkInitialized && window.FB) {
      return Promise.resolve();
    }

    if (!this.sdkLoadPromise) {
      this.sdkLoadPromise = this.loadSdkScript().then(() => this.initSdk(appId, version));
    }

    return this.sdkLoadPromise;
  }

  launchWhatsAppSignup(config: WhatsAppConfigData | null = null): Promise<WhatsAppSignupResult> {
    const appId = config?.metaAppId || META_APP_ID;
    const configId = config?.metaConfigId || META_CONFIG_ID;
    const version = config?.graphVersion || META_GRAPH_VERSION;

    if (!appId || !configId) {
      return Promise.reject(
        new Error('META_APP_ID ve META_CONFIG_ID tanımlanmalıdır.')
      );
    }

    return this.loadAndInit({ metaAppId: appId, metaConfigId: configId, graphVersion: version }).then(
      () =>
        new Promise<WhatsAppSignupResult>((resolve, reject) => {
          let phoneNumberId = '';
          let wabaId = '';
          let authCode = '';
          let alreadyRegistered = false;
          let settled = false;

          const messageHandler = (event: MessageEvent) => {
            // Meta popup'ı www./web./business.facebook.com gibi farklı origin'lerden mesaj yollayabilir.
            if (!this.isFacebookOrigin(event.origin)) {
              return;
            }

            const payload = this.parseMessagePayload(event.data);
            if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') {
              return;
            }

            const eventName = payload.event ?? '';

            if (eventName === 'CANCEL') {
              settle(false);
              cleanup();
              reject(new Error('WhatsApp bağlantısı iptal edildi.'));
              return;
            }

            if (eventName === 'ERROR') {
              settle(false);
              cleanup();
              reject(new Error(payload.data?.error_message ?? 'WhatsApp bağlantısı sırasında bir hata oluştu.'));
              return;
            }

            // FINISH, FINISH_ONLY_WABA, FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING vb.
            if (eventName.startsWith('FINISH') || payload.data?.phone_number_id || payload.data?.waba_id) {
              phoneNumberId = payload.data?.phone_number_id ?? phoneNumberId;
              wabaId = payload.data?.waba_id ?? wabaId;
              // Business app onboarding akışında numara zaten register edilmiştir.
              if (eventName === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
                alreadyRegistered = true;
              }
              tryResolve();
            }
          };

          const cleanup = () => {
            window.removeEventListener('message', messageHandler);
          };

          const settle = (value: boolean) => {
            settled = value;
          };

          const tryResolve = () => {
            // Asıl kritik değer `code`. phone_number_id / waba_id message event'inden gelir;
            // bazı akışlarda (ör. only_waba_sharing) phone_number_id gelmeyebilir.
            if (settled || !authCode) {
              return;
            }

            settled = true;
            cleanup();

            console.log('[WhatsApp Signup] phone_number_id:', phoneNumberId || '(gelmedi)');
            console.log('[WhatsApp Signup] waba_id:', wabaId || '(gelmedi)');
            console.log('[WhatsApp Signup] code:', authCode);
            console.log('[WhatsApp Signup] alreadyRegistered:', alreadyRegistered);

            resolve({
              phoneNumberId,
              wabaId,
              code: authCode,
              alreadyRegistered,
            });
          };

          window.addEventListener('message', messageHandler);

          window.FB?.login(
            (response) => {
              if (response.status === 'connected' || response.authResponse?.code) {
                authCode = response.authResponse?.code ?? '';
                tryResolve();
                return;
              }

              if (!settled) {
                settled = true;
                cleanup();
                reject(new Error('Facebook oturumu tamamlanamadı.'));
              }
            },
            {
              config_id: configId,
              response_type: 'code',
              override_default_response_type: true,
              extras: {
                setup: {},
                // Coexistence açıksa 'whatsapp_business_app_onboarding' gönderilir.
                featureType: getWhatsAppFeatureType(),
                sessionInfoVersion: '3',
              },
            }
          );
        })
    );
  }

  private isFacebookOrigin(origin: string): boolean {
    try {
      const host = new URL(origin).hostname;
      return host === 'facebook.com' || host.endsWith('.facebook.com');
    } catch {
      return false;
    }
  }

  private loadSdkScript(): Promise<void> {
    if (document.getElementById('facebook-jssdk')) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Facebook SDK yüklenemedi.'));
      document.body.appendChild(script);
    });
  }

  private initSdk(appId: string, version: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!appId) {
        reject(new Error('META_APP_ID tanımlı değil.'));
        return;
      }

      const doInit = () => {
        window.FB?.init({
          appId,
          cookie: true,
          xfbml: true,
          version,
        });
        this.sdkInitialized = true;
        resolve();
      };

      if (window.FB) {
        doInit();
        return;
      }

      window.fbAsyncInit = doInit;
    });
  }

  private parseMessagePayload(data: unknown): WaEmbeddedSignupMessage | null {
    if (!data) {
      return null;
    }

    if (typeof data === 'string') {
      try {
        return JSON.parse(data) as WaEmbeddedSignupMessage;
      } catch {
        return null;
      }
    }

    if (typeof data === 'object') {
      return data as WaEmbeddedSignupMessage;
    }

    return null;
  }
}
